import { useState } from "react";
import { useBugReports, useUpdateBugReportStatus, type BugReport } from "../../../lib/api/feedback.api";

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  open: { label: "Ouvert", color: "bg-red-500/10 text-red-500" },
  investigating: { label: "En cours", color: "bg-amber-500/10 text-amber-500" },
  fixed: { label: "Corrigé", color: "bg-green-500/10 text-green-500" },
  wontfix: { label: "Ignoré", color: "bg-ink-muted/10 text-ink-muted" },
};

const CATEGORY_LABELS: Record<string, string> = {
  ui: "Interface",
  performance: "Performance",
  data: "Données",
  broken: "Cassé",
  other: "Autre",
};

const STATUSES = ["open", "investigating", "fixed", "wontfix"] as const;

function BugReportRow({ report }: { readonly report: BugReport }) {
  const [expanded, setExpanded] = useState(false);
  const updateStatus = useUpdateBugReportStatus();
  const statusInfo = STATUS_LABELS[report.status] ?? STATUS_LABELS["open"];

  return (
    <div className="bg-surface-alt rounded-xl overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center gap-3 p-3 text-left hover:bg-surface-alt/80 transition-colors"
      >
        <span className={`px-2 py-0.5 text-[10px] font-medium rounded-full shrink-0 ${statusInfo.color}`}>
          {statusInfo.label}
        </span>
        <span className="text-sm text-ink font-medium truncate flex-1">
          {report.title}
        </span>
        <span className="text-[10px] text-ink-muted shrink-0">
          {CATEGORY_LABELS[report.category] ?? report.category}
        </span>
        <span className="text-[10px] text-ink-muted shrink-0">
          {new Date(report.createdAt).toLocaleDateString("fr-FR", {
            day: "numeric",
            month: "short",
          })}
        </span>
      </button>

      {expanded && (
        <div className="px-3 pb-3 flex flex-col gap-3 border-t border-border/30">
          <p className="text-sm text-ink mt-2 whitespace-pre-wrap">{report.description}</p>

          {report.user && (
            <p className="text-xs text-ink-muted">
              Par <span className="font-medium text-ink">{report.user.name}</span> ({report.user.email})
            </p>
          )}

          {report.systemInfo && (
            <div className="text-[11px] text-ink-muted bg-surface rounded-lg p-2 flex flex-col gap-0.5">
              <span>Version : {report.systemInfo.appVersion}</span>
              <span>Page : {report.systemInfo.currentPage}</span>
              <span>Écran : {report.systemInfo.viewport}</span>
            </div>
          )}

          <div className="flex gap-1.5 flex-wrap">
            {STATUSES.map((s) => (
              <button
                key={s}
                type="button"
                disabled={report.status === s || updateStatus.isPending}
                onClick={() => updateStatus.mutate({ id: report.id, status: s })}
                className={`px-2.5 py-1 text-xs rounded-lg border transition-colors ${
                  report.status === s
                    ? "bg-accent text-white border-accent"
                    : "border-border text-ink-muted hover:text-ink hover:border-ink-muted/30"
                }`}
              >
                {STATUS_LABELS[s]?.label ?? s}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function AdminBugReportsTab() {
  const [filter, setFilter] = useState<string | undefined>(undefined);
  const { data: reports, isLoading } = useBugReports(filter);

  if (isLoading) return <p className="text-ink-muted text-sm">Chargement...</p>;
  if (!reports?.length) return <p className="text-ink-muted text-sm">Aucun rapport de bug</p>;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-ink">
          {reports.length} rapport{reports.length > 1 ? "s" : ""}
        </p>
        <div className="flex gap-1">
          <button
            type="button"
            onClick={() => setFilter(undefined)}
            className={`px-2.5 py-1 text-xs rounded-lg transition-colors ${
              !filter ? "bg-accent text-white" : "text-ink-muted hover:bg-surface-alt"
            }`}
          >
            Tous
          </button>
          {STATUSES.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setFilter(s)}
              className={`px-2.5 py-1 text-xs rounded-lg transition-colors ${
                filter === s ? "bg-accent text-white" : "text-ink-muted hover:bg-surface-alt"
              }`}
            >
              {STATUS_LABELS[s]?.label ?? s}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        {reports.map((report) => (
          <BugReportRow key={report.id} report={report} />
        ))}
      </div>
    </div>
  );
}
